import type {
  CollectionReference,
  DocumentData,
  Firestore,
  PartialWithFieldValue,
  WithFieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  writeBatch,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  deleteDocWithError,
  getDocsWithError,
  setDocWithError,
} from '../../firestore-mobx/fetch'
import { modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { RoomStateCollaborationData } from '../../models/RoomStateCollaborationData'
import type { FirestoreRoomStateCollaborationData } from './schema'
import { schema, writeSchema } from './schema'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateCollaborationData> = {
  toFirestore: (
    data: PartialWithFieldValue<FirestoreRoomStateCollaborationData>
  ) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    const parsed = schema.parse(data)
    return parsed
  },
}

const getColRef = (
  firestore: Firestore,
  params: { roomId: string }
): CollectionReference<FirestoreRoomStateCollaborationData, DocumentData> => {
  return collection(
    firestore,
    'room_state',
    params.roomId,
    'collaboration_data'
  ).withConverter(converter)
}

export const getRoomStateCollaborationData = (
  repository: FirebaseRepository,
  params: { roomId: string }
) => {
  const ref = getColRef(repository.firestore, params)

  return modelListStream(repository, ref, RoomStateCollaborationData)
}

export const upsertRoomStateCollaborationData = (
  repository: FirebaseRepository,
  params: {
    id: string
    roomId: string
    data: WithFieldValue<FirestoreRoomStateCollaborationData>
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, { roomId: params.roomId })

  const docRef = doc(ref, params.id)

  return setDocWithError(docRef, params.data, {
    errorName: 'UpsertCollaborationDataError',
    merge: true,
  })
}

export const updateRoomStateCollaborationDataOrder = (
  repository: FirebaseRepository,
  params: {
    roomId: string
    list: {
      id: string
      order: number
      container: string
      x?: number
      y?: number
    }[]
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, { roomId: params.roomId })

  const batch = writeBatch(repository.firestore)

  params.list.forEach((obj) => {
    const docRef = doc(ref, obj.id)
    const payload: Record<string, number | string> = {
      'location.order': obj.order,
      'location.container': obj.container,
    }
    if (obj.x !== undefined) payload['location.x'] = obj.x
    if (obj.y !== undefined) payload['location.y'] = obj.y
    batch.update(docRef, payload)
  })

  return batch.commit().catch((error) => {
    throw new Error(`UpsertCollaborationDataOrderError: ${error.message}`)
  })
}

export const deleteRoomStateCollaborationData = (
  repository: FirebaseRepository,
  params: {
    id: string
    roomId: string
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, params)

  const docRef = doc(ref, params.id)

  return deleteDocWithError(docRef, 'DeleteCollaborationDataError')
}

export const deleteAllRoomStateCollaborationData = async (
  repository: FirebaseRepository,
  params: {
    roomId: string
  }
): Promise<void> => {
  const ref = getColRef(repository.firestore, params)

  const batch = writeBatch(repository.firestore)

  const docs = await getDocsWithError(ref)

  if (docs.empty) return Promise.resolve()

  for (const doc of docs.docs) {
    batch.delete(doc.ref)
  }

  return batch.commit()
}
